export interface SiteContactInfo {
  productName: string;
  contactEmail?: string | null;
  supportEmail?: string | null;
  legalEmail?: string | null;
}

export interface SiteSupportCopy {
  productName: string;
  bugReportTitle?: string;
  feedbackTitle?: string;
  attachmentHint?: string;
}

export type SiteSupportMetaCategory =
  | 'BugSeverity'
  | 'FeedbackType'
  | 'BugStatus'
  | 'FeedbackStatus'
  | 'Priority'
  | 'Workload';

export interface SiteSupportMetaOption {
  id: string;
  category: SiteSupportMetaCategory | string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface BugSummary {
  bugId: string;
  subject: string;
  submittedAt: string;
  status: string;
}

export interface SubmitBugPayload {
  subject: string;
  message: string;
  severity: string;
  repeatIssue: boolean;
  oldReferenceId?: string;
  attachmentIds?: string[];
  pageUrl?: string;
}

export interface SubmitCommentPayload {
  message: string;
  starRating?: number | null;
  feedbackType: string;
  subject?: string;
  priority?: string;
  pageUrl?: string;
}

export interface Watcher {
  userId: string;
  displayName?: string | null;
}

export interface BugListItem {
  bugId: string;
  subject: string;
  severity: string;
  status: string;
  versionFixed?: string | null;
  submittedAt: string;
  submittedBy: string;
  assigneeUserId?: string | null;
  assigneeDisplayName?: string | null;
  workload?: string | null;
}

export interface BugDetail extends BugListItem {
  message: string;
  repeatIssue: boolean;
  oldReferenceId?: string | null;
  pageUrl?: string | null;
  submittedByDisplayName?: string | null;
  roleAtSubmit?: string | null;
  fixedAt?: string | null;
  fixedBy?: string | null;
  resolutionNotes?: string | null;
  ticketUrl?: string | null;
  claimedAt?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  watchers: Watcher[];
  attachments: { attachmentId: string; fileName: string; contentType: string; sizeBytes: number }[];
}

export interface CommentListItem {
  id: string;
  publicId: string;
  submittedAt: string;
  subject: string;
  messagePreview: string;
  starRating?: number | null;
  feedbackType: string;
  priority: string;
  status: string;
  submittedBy: string;
  assigneeUserId?: string | null;
  assigneeDisplayName?: string | null;
  pageUrl?: string | null;
}

export interface CommentDetail extends CommentListItem {
  message: string;
  submittedByDisplayName?: string | null;
  roleAtSubmit?: string | null;
  claimedAt?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  workload?: string | null;
  ticketUrl?: string | null;
  watchers: Watcher[];
}

export interface DiscussionMessage {
  id: string;
  targetKind: string;
  targetId: string;
  parentMessageId?: string | null;
  authorUserId: string;
  authorDisplayName?: string | null;
  body: string;
  createdAt: string;
  editedAt?: string | null;
}

export interface PagedResult<T> {
  total: number;
  items: T[];
}

export interface ChartSlice {
  label: string;
  count: number;
}

export interface BugMetrics {
  bySeverity: ChartSlice[];
  byStatus: ChartSlice[];
}

export interface FeedbackMetrics {
  averageRating: number;
  totalResponses: number;
  ratingDistribution: ChartSlice[];
  byType: ChartSlice[];
  overTime: { date: string; count: number }[];
}

export interface UpdateBugAdminPayload {
  status?: string;
  versionFixed?: string;
  resolutionNotes?: string;
  ticketUrl?: string;
  assigneeUserId?: string;
  assigneeDisplayName?: string;
  startAt?: string | null;
  endAt?: string | null;
  workload?: string | null;
  severity?: string;
  clearAssignee?: boolean;
}

export interface UpdateCommentAdminPayload {
  status?: string;
  priority?: string;
  subject?: string;
  message?: string;
  ticketUrl?: string;
  assigneeUserId?: string;
  assigneeDisplayName?: string;
  startAt?: string | null;
  endAt?: string | null;
  workload?: string | null;
  clearAssignee?: boolean;
}

export interface UpdateMyTicketPayload {
  message?: string;
  subject?: string;
}

export interface CreateSiteSupportMetaPayload {
  category: SiteSupportMetaCategory | string;
  name: string;
  sortOrder?: number;
}

export interface UpdateSiteSupportMetaPayload {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export type FeedbackTargetKind = 'bug' | 'feedback';

export interface SiteFeedbackApi {
  submitBug(payload: SubmitBugPayload): Promise<{ bugId: string }>;
  submitComment(payload: SubmitCommentPayload): Promise<{ publicId: string; id: string }>;
  uploadAttachment(file: File): Promise<{ attachmentId: string; fileName: string; size: number }>;
  searchBugs(query?: string): Promise<BugSummary[]>;
  listBugs(skip?: number, take?: number): Promise<PagedResult<BugListItem>>;
  getBug(bugId: string): Promise<BugDetail>;
  updateBug(bugId: string, payload: UpdateBugAdminPayload): Promise<void>;
  claimBug(bugId: string, force?: boolean): Promise<void>;
  deleteBug(bugId: string): Promise<void>;
  getBugMetrics(): Promise<BugMetrics>;
  listComments(skip?: number, take?: number): Promise<PagedResult<CommentListItem>>;
  getComment(commentId: string): Promise<CommentDetail>;
  updateComment(commentId: string, payload: UpdateCommentAdminPayload): Promise<void>;
  claimComment(commentId: string, force?: boolean): Promise<void>;
  deleteComment(commentId: string): Promise<void>;
  getFeedbackMetrics(): Promise<FeedbackMetrics>;
  listMyBugs(skip?: number, take?: number): Promise<PagedResult<BugListItem>>;
  listMyFeedback(skip?: number, take?: number): Promise<PagedResult<CommentListItem>>;
  getMyBug(bugId: string): Promise<BugDetail>;
  getMyFeedback(publicId: string): Promise<CommentDetail>;
  updateMyBug(bugId: string, payload: UpdateMyTicketPayload): Promise<void>;
  updateMyFeedback(publicId: string, payload: UpdateMyTicketPayload): Promise<void>;
  watchTicket(kind: FeedbackTargetKind, id: string): Promise<void>;
  unwatchTicket(kind: FeedbackTargetKind, id: string): Promise<void>;
  listDiscussion(kind: FeedbackTargetKind, id: string): Promise<DiscussionMessage[]>;
  postDiscussion(kind: FeedbackTargetKind, id: string, body: string): Promise<DiscussionMessage>;
  deleteDiscussionMessage(messageId: string): Promise<void>;
  listActiveMeta(category: SiteSupportMetaCategory | string): Promise<SiteSupportMetaOption[]>;
  listAdminMeta(category?: SiteSupportMetaCategory | string): Promise<SiteSupportMetaOption[]>;
  createMeta(payload: CreateSiteSupportMetaPayload): Promise<SiteSupportMetaOption>;
  updateMeta(id: string, payload: UpdateSiteSupportMetaPayload): Promise<SiteSupportMetaOption>;
  recordCookieConsent(choice: string): Promise<void>;
  fetchSiteContact(): Promise<SiteContactInfo>;
  attachmentUrl(attachmentId: string, opts?: { inline?: boolean; download?: boolean }): string;
}

export interface VersionComponentMetadata {
  name?: string | null;
  description?: string | null;
  buildName?: string | null;
  buildVersion?: string | null;
  runtime?: string | null;
}

/** @deprecated Use VersionComponentMetadata — kept for older injected builds. */
export interface VersionJsonMetadata {
  name?: string | null;
  productVersion?: string | null;
  buildVersion?: string | null;
  longVersion?: string | null;
  npmVersion?: string | null;
}

export interface BuildMetadata {
  version?: string;
  productVersion?: string;
  buildVersion?: string;
  name?: string;
  buildTime?: string;
  gitCommit?: string;
  site?: VersionComponentMetadata | null;
  api?: VersionComponentMetadata | null;
  web?: VersionComponentMetadata | null;
  /** Legacy alias for `web`. */
  ui?: VersionComponentMetadata | VersionJsonMetadata | null;
}

export function readBuildMetadata(): BuildMetadata {
  try {
    const el = document.getElementById('build-metadata');
    if (!el?.textContent?.trim()) return {};
    return JSON.parse(el.textContent) as BuildMetadata;
  } catch {
    return {};
  }
}

export function isImageAttachmentContentType(contentType: string | null | undefined): boolean {
  return Boolean(contentType?.toLowerCase().startsWith('image/'));
}
